/**
 * @fileoverview Harita Marker Yonetimi Hook'u
 *
 * Bu modul, Mapbox haritasinda ilan marker'larini olusturmak ve yonetmek
 * icin kullanilan custom hook'u icerir. Marker olusturma, stil guncelleme,
 * hover/click etkilesimleri ve secim durumu yonetimini saglar.
 *
 * @module useMapMarkers
 * @requires mapbox-gl
 */

import { useEffect, useRef, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import {
  isValidCoordinate,
  computeValidCoords,
  createMarkerElement,
  updateMarkerStyle,
  fitMapToMarkers,
  MARKER_COLOR,
  MARKER_COLOR_HOVER,
} from "../utils";
import { formatShortPrice } from "../utils/format";

/**
 * Marker olay dinleyicilerini (event handlers) olusturan fabrika fonksiyonu.
 *
 * Bu fonksiyon, closure yukunu azaltmak icin handler olusturmayi
 * render dongusunden ayirir. Her marker icin mouseenter, mouseleave
 * ve click olaylarini yonetir.
 *
 * @private
 * @function createMarkerHandlers
 * @param {HTMLElement} el - Marker DOM elementi
 * @param {string} listingId - Ilan ID'si
 * @param {React.RefObject<string|null>} selectedIdRef - Secili ilan ID ref'i (dinamik okuma icin)
 * @param {Object} listing - Ilan nesnesi
 * @param {React.RefObject<Function>} onSelectRef - Secim callback ref'i
 * @param {React.RefObject<HTMLElement>} containerRef - Konteyner ref'i
 * @returns {Object} Event handler nesnesi
 * @returns {Function} returns.mouseenter - Mouse enter handler
 * @returns {Function} returns.mouseleave - Mouse leave handler
 * @returns {Function} returns.click - Click handler
 */
function createMarkerHandlers(el, listingId, selectedIdRef, listing, onSelectRef, containerRef) {
  const handleMouseEnter = () => {
    // Read current selectedId from ref at event time (not stale closure)
    if (listingId !== selectedIdRef?.current) {
      el.style.boxShadow = "0 4px 12px rgba(0,0,0,0.4)";
      el.style.background = MARKER_COLOR_HOVER;
    }
  };

  const handleMouseLeave = () => {
    // Read current selectedId from ref at event time
    if (listingId !== selectedIdRef?.current) {
      el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.3)";
      el.style.background = MARKER_COLOR;
    }
  };

  const handleClick = () => {
    if (listing && onSelectRef.current) {
      const rect = el.getBoundingClientRect();
      const container = containerRef?.current;
      const containerRect = container?.getBoundingClientRect();

      if (containerRect) {
        const screenPosition = {
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
        };
        onSelectRef.current({ ...listing, _screenPosition: screenPosition });
      } else {
        onSelectRef.current(listing);
      }
    }
  };

  return {
    mouseenter: handleMouseEnter,
    mouseleave: handleMouseLeave,
    click: handleClick,
  };
}

/**
 * @typedef {Object} MarkerData
 * @property {mapboxgl.Marker} marker - Mapbox marker instance
 * @property {HTMLElement} element - Marker DOM elementi
 * @property {[number, number]} coordinate - [lng, lat] koordinat
 * @property {Object} listeners - Event listener referanslari
 */

/**
 * @typedef {Object} UseMapMarkersOptions
 * @property {mapboxgl.Map|null} map - Mapbox harita instance'i
 * @property {boolean} mapReady - Harita hazir mi
 * @property {Array<Object>} listings - Ilan nesneleri dizisi
 * @property {string|null} selectedListingId - Secili ilan ID'si
 * @property {Function} onSelectListing - Ilan secildiginde callback
 * @property {React.RefObject<HTMLElement>} containerRef - Konteyner ref'i
 */

/**
 * @typedef {Object} UseMapMarkersReturn
 * @property {Array<[number, number]>} coords - Gecerli koordinatlar dizisi
 * @property {React.RefObject<Map>} markersMap - Marker ID -> MarkerData map'i
 */

/**
 * Mapbox haritasinda marker'lari yonetir.
 *
 * Bu hook, ilanlarin harita uzerinde marker olarak gosterilmesini yonetir.
 * Her marker fiyat etiketi gosterir ve tiklanabilir/hover edilebilir.
 *
 * **Temel Islevler:**
 * - Ilanlar icin marker olusturma ve DOM ekleme
 * - Hover efektleri (renk ve golge degisimi)
 * - Click ile ilan secimi
 * - Secili marker'a fly-to animasyonu
 * - Marker stili guncelleme (secim durumuna gore)
 * - Cleanup (event listener ve marker temizligi)
 *
 * **Performans Optimizasyonlari:**
 * - Listing ID'leri memoize edilir
 * - Event handler'lar fabrika fonksiyonu ile olusturulur
 * - Callback ref'leri stale closure'u onler
 *
 * **Onemli:** Marker'lar listings dizisi degistiginde yeniden olusturulur.
 * Secim degisikliklerinde sadece stil guncellenir (marker yeniden olusturulmaz).
 *
 * @function useMapMarkers
 * @param {UseMapMarkersOptions} options - Hook opsiyonlari
 * @returns {UseMapMarkersReturn} Koordinatlar ve marker map'i
 *
 * @example
 * const { coords, markersMap } = useMapMarkers({
 *   map,
 *   mapReady,
 *   listings: searchOutput.listings,
 *   selectedListingId,
 *   onSelectListing: (listing) => handleDetailClick(listing.id),
 *   containerRef: mapContainerRef,
 * });
 */
export function useMapMarkers({
  map,
  mapReady,
  listings,
  selectedListingId,
  onSelectListing,
  containerRef,
}) {
  const markersMap = useRef(new Map());
  const onSelectListingRef = useRef(onSelectListing);
  const selectedListingIdRef = useRef(selectedListingId);

  // Keep callback ref updated
  useEffect(() => {
    onSelectListingRef.current = onSelectListing;
  }, [onSelectListing]);

  // Keep selectedListingId ref updated (for event handlers to read current value)
  useEffect(() => {
    selectedListingIdRef.current = selectedListingId;
  }, [selectedListingId]);

  // Stable listing IDs for dependency tracking
  const listingIds = useMemo(() => {
    return (Array.isArray(listings) ? listings : [])
      .map((l) => l?.id)
      .filter(Boolean)
      .join(",");
  }, [listings]);

  // Compute valid coordinates
  const coords = useMemo(() => computeValidCoords(listings), [listings]);

  // Add/update markers when listings change
  // Note: selectedListingId is NOT in deps - selection styling is handled by separate effect
  useEffect(() => {
    if (!map || !mapReady) return;

    // Remove old markers and their event listeners
    markersMap.current.forEach(({ marker, element, listeners }) => {
      if (listeners) {
        element.removeEventListener("mouseenter", listeners.mouseenter);
        element.removeEventListener("mouseleave", listeners.mouseleave);
        element.removeEventListener("click", listeners.click);
      }
      marker.remove();
    });
    markersMap.current.clear();

    // Add new markers (created with default unselected style)
    (Array.isArray(listings) ? listings : []).forEach((listing) => {
      const c = listing?.coordinate;
      if (!c || !isValidCoordinate(c.lon, c.lat)) return;

      const listingId = listing?.id;
      const priceLabel = formatShortPrice(listing?.price);

      // Always create with unselected style - selection effect will update if needed
      const el = createMarkerElement(priceLabel, false);

      // Create event handlers using factory
      // Note: selectedId is read from ref at event time (not stale closure)
      const listeners = createMarkerHandlers(
        el,
        listingId,
        selectedListingIdRef,
        listing,
        onSelectListingRef,
        containerRef
      );

      // Attach event listeners
      el.addEventListener("mouseenter", listeners.mouseenter);
      el.addEventListener("mouseleave", listeners.mouseleave);
      el.addEventListener("click", listeners.click);

      const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
        .setLngLat([c.lon, c.lat])
        .addTo(map);

      if (listingId) {
        markersMap.current.set(listingId, {
          marker,
          element: el,
          coordinate: [c.lon, c.lat],
          listeners,
        });
      }
    });

    // Fit map to markers
    if (coords.length > 0) {
      fitMapToMarkers(map, coords);
    }
  }, [listingIds, mapReady, map, coords, containerRef]);

  // Update marker styles when selection changes
  useEffect(() => {
    if (!map || !mapReady) return;

    markersMap.current.forEach(({ element }, id) => {
      updateMarkerStyle(element, id === selectedListingId);
    });

    // Fly to selected marker
    if (selectedListingId) {
      const selected = markersMap.current.get(selectedListingId);
      if (selected?.coordinate) {
        map.flyTo({
          center: selected.coordinate,
          zoom: Math.max(map.getZoom(), 13),
          duration: 800,
          essential: true,
        });
      }
    }
  }, [selectedListingId, mapReady, map]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      markersMap.current.forEach(({ marker, element, listeners }) => {
        if (listeners) {
          element.removeEventListener("mouseenter", listeners.mouseenter);
          element.removeEventListener("mouseleave", listeners.mouseleave);
          element.removeEventListener("click", listeners.click);
        }
        marker.remove();
      });
      markersMap.current.clear();
    };
  }, []);

  return { coords, markersMap };
}

export default useMapMarkers;
